import { useState } from 'react';
import {
  motion,
  AnimatePresence,
  useScroll,
  useMotionValueEvent,
  useReducedMotion,
} from 'motion/react';
import { useTranslation } from 'react-i18next';
import { HiOutlineMenuAlt4, HiOutlineX } from 'react-icons/hi';
import { EASE } from '../lib/motion';
import { useReady } from '../lib/ready';

const links = ['work', 'services', 'process', 'studio'] as const;

export function Navbar() {
  const { t, i18n } = useTranslation('common');
  const reduce = useReducedMotion();
  const ready = useReady();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { scrollY } = useScroll();

  // State flips only when crossing the threshold, not on every scroll frame.
  useMotionValueEvent(scrollY, 'change', (v) => {
    const next = v > 24;
    setScrolled((prev) => (prev === next ? prev : next));
  });

  const toggleLang = () =>
    i18n.changeLanguage(i18n.language === 'en' ? 'el' : 'en');

  return (
    <>
      <motion.header
        initial={reduce ? false : { y: -80 }}
        animate={ready ? { y: 0 } : undefined}
        transition={{ duration: 0.9, ease: EASE, delay: 0.35 }}
        className="fixed inset-x-0 top-0 z-50"
      >
        <nav
          className={`glass mx-auto flex h-16 max-w-[1400px] items-center justify-between px-5 transition-colors duration-300 md:px-8 ${
            scrolled
              ? 'border-b border-line bg-base/70 backdrop-blur-xl'
              : 'border-b border-transparent'
          }`}
        >
          <a
            href="#top"
            className="font-display text-xl font-semibold tracking-tight"
          >
            kouk<span className="text-accent">.gr</span>
          </a>

          <div className="hidden items-center gap-8 md:flex">
            {links.map((id) => (
              <a
                key={id}
                href={`#${id}`}
                className="group relative text-sm text-muted transition-colors hover:text-fg"
              >
                {t(`nav.${id}`)}
                <span className="absolute -bottom-1 left-0 h-px w-full origin-left scale-x-0 bg-accent transition-transform duration-300 ease-out group-hover:scale-x-100" />
              </a>
            ))}
          </div>

          <div className="flex items-center gap-2">
            {/* <button
              onClick={toggleLang}
              className="rounded-full border border-line-strong px-3 py-1.5 font-mono text-xs text-muted transition-colors hover:border-accent hover:text-accent"
              aria-label="Switch language"
            >
              {i18n.language === 'en' ? 'ΕΛ' : 'EN'}
            </button> */}
            <a
              href="#contact"
              className="hidden rounded-full bg-accent px-5 py-2 text-sm font-medium text-on-accent transition-colors hover:bg-[#8fd3c7] md:inline-flex"
            >
              {t('nav.cta')}
            </a>
            <button
              onClick={() => setOpen(true)}
              className="text-fg md:hidden"
              aria-label="Open menu"
            >
              <HiOutlineMenuAlt4 size={26} />
            </button>
          </div>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[70] flex flex-col bg-base/95 backdrop-blur-xl md:hidden"
          >
            <div className="flex h-16 items-center justify-between px-5">
              <span className="font-display text-xl font-semibold tracking-tight">
                kouk<span className="text-accent">.gr</span>
              </span>
              <button
                onClick={() => setOpen(false)}
                className="text-fg"
                aria-label="Close menu"
              >
                <HiOutlineX size={26} />
              </button>
            </div>
            <div className="flex flex-1 flex-col justify-center gap-2 px-6">
              {[...links, 'contact'].map((id, i) => (
                <motion.a
                  key={id}
                  href={`#${id}`}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i + 0.1, ease: EASE }}
                  className="font-display text-4xl font-semibold tracking-tight text-fg"
                >
                  {t(`nav.${id}`)}
                </motion.a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
